<template>
  <v-card class="emergency-card" elevation="4">
    <!-- Loading state -->
    <v-overlay :value="loadingUserInfo" absolute>
      <v-progress-circular
        indeterminate
        size="64"
        color="primary"
      ></v-progress-circular>
    </v-overlay>

    <!-- Header de emergencia -->
    <v-card-title class="emergency-header white--text">
      <v-icon left color="white">mdi-hospital-box</v-icon>
      <span class="text-h6 font-weight-bold">FICHA MÉDICA DE EMERGENCIA</span>
      <v-spacer></v-spacer>
      <v-chip color="error" text-color="white" small>
        <v-icon left small>mdi-alert</v-icon>
        URGENTE
      </v-chip>
    </v-card-title>

    <v-card-text class="pa-4" v-if="userInfo">
      <v-row>
        <!-- Información Personal Crítica -->
        <v-col cols="12" md="6">
          <v-card outlined class="info-section">
            <v-card-subtitle class="pb-2">
              <v-icon left color="primary">mdi-account</v-icon>
              <strong>DATOS PERSONALES</strong>
            </v-card-subtitle>
            <v-card-text class="pt-0">
              <div class="info-row">
                <span class="label">Nombre:</span>
                <span class="value font-weight-bold">{{ userInfo.name }}</span>
              </div>
              <div class="info-row">
                <span class="label">Edad:</span>
                <span class="value">{{ calculateAge(userInfo.info.nacimiento) }} años</span>
              </div>
              <div class="info-row">
                <span class="label">Género:</span>
                <span class="value">{{ getGender(userInfo.info.genero) }}</span>
              </div>
              <div class="info-row">
                <span class="label">Teléfono:</span>
                <span class="value font-weight-bold text--primary">{{ formatPhone(userInfo.info.telefono) }}</span>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Información Redes -->
        <v-col cols="12">
          <v-card outlined class="info-section vital-signs">
            <v-card-subtitle class="pb-2">
              <v-icon left color="red">mdi-account-alert-outline</v-icon>
              <strong>RED DE EMERGENCIA</strong>
            </v-card-subtitle>
            <v-card-text class="pt-0">
              <v-row>
                <v-col cols="12" sm="6" md="3" v-for="user in emergency_network" :key="user.id">
                  <div class="vital-card">
                    <div class="vital-label">{{ user.name }}</div>
                    <div class="vital-range"><a :href="'tel:' + user.phone">{{ formatPhone( user.phone ) }}</a></div>
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Datos Físicos -->
        <v-col cols="12" md="6">
          <v-card outlined class="info-section">
            <v-card-subtitle class="pb-2">
              <v-icon left color="teal">mdi-human</v-icon>
              <strong>DATOS FÍSICOS</strong>
            </v-card-subtitle>
            <v-card-text class="pt-0">
              <v-row>
                <v-col cols="6">
                  <div class="metric-card">
                    <div class="metric-label">Estatura</div>
                    <div class="metric-value">{{ userInfo.ficha.estatura }} cm</div>
                  </div>
                </v-col>
                <v-col cols="6">
                  <div class="metric-card">
                    <div class="metric-label">Peso</div>
                    <div class="metric-value">{{ userInfo.ficha.peso }} kg</div>
                  </div>
                </v-col>
                <v-col cols="12">
                  <div class="metric-card">
                    <div class="metric-label">IMC</div>
                    <div class="metric-value">
                      {{ calculateIMC(userInfo.ficha.peso, userInfo.ficha.estatura) }}
                      <v-chip 
                        small 
                        :color="getIMCColor(calculateIMC(userInfo.ficha.peso, userInfo.ficha.estatura))"
                        text-color="white"
                        class="ml-2"
                      >
                        {{ getIMCCategory(calculateIMC(userInfo.ficha.peso, userInfo.ficha.estatura)) }}
                      </v-chip>
                    </div>
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Signos Vitales de Referencia -->
        <v-col cols="12">
          <v-card outlined class="info-section vital-signs">
            <v-card-subtitle class="pb-2">
              <v-icon left color="red">mdi-heart-pulse</v-icon>
              <strong>SIGNOS VITALES DE REFERENCIA</strong>
            </v-card-subtitle>
            <v-card-text class="pt-0">
              <v-row>
                <v-col cols="12" sm="6" md="3">
                  <div class="vital-card">
                    <v-icon color="red" size="20">mdi-heart</v-icon>
                    <div class="vital-label">Presión Sistólica</div>
                    <div class="vital-range">{{ userInfo.references.min_sistolica }} - {{ userInfo.references.max_sistolica }} mmHg</div>
                  </div>
                </v-col>
                <v-col cols="12" sm="6" md="3">
                  <div class="vital-card">
                    <v-icon color="blue" size="20">mdi-heart-outline</v-icon>
                    <div class="vital-label">Presión Diastólica</div>
                    <div class="vital-range">{{ userInfo.references.min_diastolica }} - {{ userInfo.references.max_diastolica }} mmHg</div>
                  </div>
                </v-col>
                <v-col cols="12" sm="6" md="3">
                  <div class="vital-card">
                    <v-icon color="orange" size="20">mdi-pulse</v-icon>
                    <div class="vital-label">Ritmo Cardíaco</div>
                    <div class="vital-range">{{ userInfo.references.min_ritmo }} - {{ userInfo.references.max_ritmo }} bpm</div>
                  </div>
                </v-col>
                <v-col cols="12" sm="6" md="3">
                  <div class="vital-card">
                    <v-icon color="cyan" size="20">mdi-lungs</v-icon>
                    <div class="vital-label">Oxígeno (min)</div>
                    <div class="vital-range">≥ {{ userInfo.references.min_oxigeno }}%</div>
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Ubicación -->
        <!-- <v-col cols="12" md="8">
          <v-card outlined class="info-section">
            <v-card-subtitle class="pb-2">
              <v-icon left color="green">mdi-map-marker</v-icon>
              <strong>UBICACIÓN</strong>
            </v-card-subtitle>
            <v-card-text class="pt-0">
              <div class="info-row">
                <span class="label">Dirección:</span>
                <span class="value font-weight-bold">{{ userInfo.info.direccion }}</span>
              </div>
              <div class="info-row">
                <span class="label">Comuna:</span>
                <span class="value">{{ userInfo.info.comuna_data.comuna }}</span>
              </div>
              <div class="info-row">
                <span class="label">Región:</span>
                <span class="value">{{ userInfo.info.comuna_data.provincia.region.region }}</span>
              </div>
            </v-card-text>
          </v-card>
        </v-col> -->

        <!-- Estado de Alerta -->
        <v-col cols="12" md="4">
          <v-card outlined class="info-section alert-section">
            <v-card-subtitle class="pb-2">
              <v-icon left color="warning">mdi-alert-circle</v-icon>
              <strong>ESTADO</strong>
            </v-card-subtitle>
            <v-card-text class="pt-0">
              <v-chip 
                large 
                :color="getStatusColor(userInfo.info.estado)"
                text-color="white"
                class="mb-2"
              >
                <v-icon left>{{ getStatusIcon(userInfo.info.estado) }}</v-icon>
                {{ getStatusText(userInfo.info.estado) }}
              </v-chip>
              <div class="text-caption">
                Última actualización: {{ formatDate(userInfo.updated_at) }}
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import { getUserPublicInfo } from '@/helpers/api/user'
export default {
  name: 'UserPublicInfo',
	computed: {
		emergency_network(){
			if(this.userInfo && this.userInfo.groups_anfitrion){
				const data = this.userInfo.groups_anfitrion
				const usuarios = data.map(item => {
					const persona = item.personas[0];
					return {
						name: persona.name,
						phone: persona.info?.telefono || null
					};
				});
				console.log('usuarios', usuarios)
				return usuarios
			}
			return null
		}
	},
  data() {
    return {
      userId: null,
      userInfo: null,
      loadingUserInfo: false
    }
  },
  mounted() {
    if(this.$route.query && this.$route.query.user_id) {
      this.userId = this.$route.query.user_id
      this.getUserInfo()
    }
  },
  methods: {
    async getUserInfo() {
      this.loadingUserInfo = true
      const user = await getUserPublicInfo({user_id: this.userId})
      if(user.code) {
        this.userInfo = user.user
        this.loadingUserInfo = false
      }
    },
    
    calculateAge(birthDate) {
      const today = new Date();
      const birth = new Date(birthDate);
      let age = today.getFullYear() - birth.getFullYear();
      const monthDiff = today.getMonth() - birth.getMonth();
      
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
        age--;
      }
      
      return age;
    },
    
    getGender(genderId) {
      const genders = {
        1: 'Masculino',
        2: 'Femenino',
        3: 'Otro'
      };
      return genders[genderId] || 'No especificado';
    },
    
    formatPhone(phone) {
      // Formato chileno: +56 9 XXXX XXXX
      if (phone && phone.startsWith('56')) {
        const number = phone.substring(2);
        return `+56 ${number.substring(0, 1)} ${number.substring(1, 5)} ${number.substring(5)}`;
      }
      return phone;
    },
    
    calculateIMC(weight, height) {
      const heightInMeters = height / 100;
      const imc = weight / (heightInMeters * heightInMeters);
      return imc.toFixed(1);
    },
    
    getIMCColor(imc) {
      const imcValue = parseFloat(imc);
      if (imcValue < 18.5) return 'blue';
      if (imcValue < 25) return 'green';
      if (imcValue < 30) return 'orange';
      return 'red';
    },
    
    getIMCCategory(imc) {
      const imcValue = parseFloat(imc);
      if (imcValue < 18.5) return 'Bajo peso';
      if (imcValue < 25) return 'Normal';
      if (imcValue < 30) return 'Sobrepeso';
      return 'Obesidad';
    },
    
    getStatusColor(status) {
      const colors = {
        1: 'success',
        2: 'warning',
        3: 'error'
      };
      return colors[status] || 'grey';
    },
    
    getStatusIcon(status) {
      const icons = {
        1: 'mdi-check-circle',
        2: 'mdi-alert-circle',
        3: 'mdi-close-circle'
      };
      return icons[status] || 'mdi-help-circle';
    },
    
    getStatusText(status) {
      const texts = {
        1: 'ACTIVO',
        2: 'PRECAUCIÓN',
        3: 'CRÍTICO'
      };
      return texts[status] || 'DESCONOCIDO';
    },
    
    formatDate(dateString) {
      return new Date(dateString).toLocaleString('es-CL');
    }
  }
}
</script>

<style scoped>
.emergency-card {
  max-width: 100%;
  margin: 0 auto;
}

.emergency-header {
  background: linear-gradient(135deg, #d32f2f 0%, #f44336 100%);
}

.info-section {
  height: 100%;
  border-left: 4px solid #2196F3;
}

.vital-signs {
  border-left: 4px solid #f44336;
}

.alert-section {
  border-left: 4px solid #ff9800;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  padding: 4px 0;
  border-bottom: 1px solid #f0f0f0;
}

.label {
  font-weight: 500;
  color: #666;
  min-width: 80px;
}

.value {
  color: #333;
  text-align: right;
}

.metric-card {
  text-align: center;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.metric-label {
  font-size: 12px;
  color: #666;
  text-transform: uppercase;
  font-weight: 500;
}

.metric-value {
  font-size: 18px;
  font-weight: bold;
  color: #333;
  margin-top: 4px;
}

.vital-card {
  text-align: center;
  padding: 16px 12px;
  background: #fafafa;
  border-radius: 12px;
  border: 2px solid #f0f0f0;
  transition: all 0.3s ease;
}

.vital-card:hover {
  border-color: #2196F3;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
}

.vital-label {
  font-size: 11px;
  color: #666;
  text-transform: uppercase;
  font-weight: 600;
  margin: 8px 0 4px;
  line-height: 1.2;
}

.vital-range {
  font-size: 14px;
  font-weight: bold;
  color: #333;
}

.emergency-contacts {
  border-left: 4px solid #ff9800;
}

.contact-card {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 16px;
  border: 2px solid #e9ecef;
  transition: all 0.3s ease;
}

.contact-card:hover {
  border-color: #ff9800;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
}

.contact-header {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
}

.contact-name {
  font-weight: bold;
  font-size: 16px;
  color: #333;
  margin-left: 8px;
  flex: 1;
}

.contact-details {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.contact-phone,
.contact-location {
  display: flex;
  align-items: center;
  font-size: 14px;
  color: #666;
}

.contact-phone span,
.contact-location span {
  margin-left: 8px;
}

.contact-phone {
  font-weight: 600;
  color: #2e7d32;
}

.no-data-message {
  text-align: center;
  padding: 40px 20px;
  background: #fafafa;
  border-radius: 12px;
  border: 2px dashed #e0e0e0;
}

@media (max-width: 600px) {
  .emergency-header .text-h6 {
    font-size: 14px !important;
  }
  
  .info-row {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .value {
    text-align: left;
    margin-top: 2px;
  }
  
  .metric-value {
    font-size: 16px;
  }
  
  .vital-range {
    font-size: 12px;
  }
}
</style>